<script setup lang="ts">
/**
 * One headline figure.
 *
 * The number is the biggest thing on it and uses tabular figures, so a column
 * of tiles lines up and a changing value does not make the layout twitch.
 * `hint` exists because a figure with no definition is a figure two people in
 * the room understand differently.
 */

defineProps<{
  label: string;
  value: string;
  hint?: string | undefined;
  tone?: "plain" | "good" | "warn" | "bad";
}>();
</script>

<template>
  <div class="tile" :data-tone="tone ?? 'plain'">
    <span class="tile__label">{{ label }}</span>
    <strong class="tile__value" :style="{ '--chars': String(Math.max(value.length, 6)) }">{{ value }}</strong>
    <span v-if="hint" class="tile__hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
/* A card on the page: surface, hairline, no shadow — Monday's panel. */
.tile {
  /* The figure sizes itself to the tile's width (`cqi`), see `.tile__value`. */
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-1);
  padding: var(--gf-space-4) var(--gf-space-5);
  min-width: 0;
  background: var(--gf-color-surface);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
}

/* Sentence case and quieter than the figure: read once, then skipped. */
.tile__label {
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text-muted);
}

/*
 * ONE LINE, ALWAYS. `overflow-wrap: anywhere` broke "162 424,40 Kč" into
 * "162 424,4" and "0 Kč" on a phone — a figure split across lines is two
 * wrong figures. Instead the size gives way: never above the ramp's 2xl,
 * never wider than the tile, from the value's own length (`--chars`, about
 * 0.6 em per tabular digit).
 */
.tile__value {
  font-size: min(var(--gf-text-2xl), calc(100cqi / (var(--chars) * 0.6)));
  font-weight: var(--gf-weight-semibold);
  line-height: var(--gf-leading-tight);
  letter-spacing: var(--gf-tracking-tight);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.tile__hint {
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
  line-height: var(--gf-leading-normal);
}

/*
  Tone is a TINTED CARD with the figure in the tone's own ink — never colour
  alone: the tint also moves the card's lightness, and the label and hint
  carry the words ("Finanční správa", "Nic nečeká…").

  It used to add a 3 px rule down the left edge as well. A tinted card
  already reads as a state across a desk, and the stripe read as a border.
  The edge stays the theme's own `borderSubtle`: a tone-tinted edge came out
  pale in high contrast, where every card's edge is meant to be black.
*/
.tile[data-tone="good"] {
  background: var(--gf-color-success-surface);
}
.tile[data-tone="good"] .tile__value {
  color: var(--gf-color-success);
}
.tile[data-tone="warn"] {
  background: var(--gf-color-warning-surface);
}
.tile[data-tone="warn"] .tile__value {
  color: var(--gf-color-warning);
}
.tile[data-tone="bad"] {
  background: var(--gf-color-danger-surface);
}
.tile[data-tone="bad"] .tile__value {
  color: var(--gf-color-danger);
}
</style>
